import { Card } from '@/components/ui/card';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-stone-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="p-8 md:p-12">
          <h1 className="text-4xl font-bold text-stone-900 mb-6">Privacy Policy</h1>
          <p className="text-sm text-stone-500 mb-8">Last Updated: January 2025</p>

          <div className="prose prose-stone max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-4">1. Introduction</h2>
              <p className="text-stone-700 leading-relaxed">
                Garden & Greenhouse ("we," "us," or "our") respects your privacy and is committed to protecting your 
                personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you visit our website and use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-4">2. Information We Collect</h2>
              <p className="text-stone-700 leading-relaxed mb-4">We may collect the following types of information:</p>
              
              <h3 className="text-xl font-semibold text-stone-900 mt-6 mb-3">Personal Information</h3>
              <ul className="list-disc list-inside text-stone-700 space-y-2">
                <li>Name and email address (newsletter subscriptions)</li>
                <li>Business contact information (for Garden Hub listings)</li>
                <li>Phone number (optional, for business listings)</li>
                <li>Payment information (if applicable, processed by third parties)</li>
              </ul>

              <h3 className="text-xl font-semibold text-stone-900 mt-6 mb-3">Automatically Collected Information</h3>
              <ul className="list-disc list-inside text-stone-700 space-y-2">
                <li>IP address and browser type</li>
                <li>Device information</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website addresses</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>

              <h3 className="text-xl font-semibold text-stone-900 mt-6 mb-3">AI Chat Data</h3>
              <ul className="list-disc list-inside text-stone-700 space-y-2">
                <li>Chat messages and questions submitted to our AI assistant</li>
                <li>Images uploaded for plant identification</li>
                <li>Session information for conversation continuity</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-stone-700 leading-relaxed mb-4">We use collected information for:</p>
              <ul className="list-disc list-inside text-stone-700 space-y-2">
                <li>Providing and improving our services</li>
                <li>Sending newsletters and updates (with your consent)</li>
                <li>Processing business listing applications</li>
                <li>Responding to your inquiries and requests</li>
                <li>Analyzing website usage and improving user experience</li>
                <li>Providing personalized AI gardening assistance</li>
                <li>Detecting and preventing fraud or abuse</li>
                <li>Complying with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-4">4. Cookies and Tracking Technologies</h2>
              <p className="text-stone-700 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your experience. Cookies are small data files 
                stored on your device. Types of cookies we use:
              </p>
              <ul className="list-disc list-inside text-stone-700 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for website functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                <li><strong>Affiliate Cookies:</strong> Track purchases through our affiliate links</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              </ul>
              <p className="text-stone-700 leading-relaxed mt-4">
                You can control cookies through your browser settings. However, disabling cookies may affect site functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-4">5. Third-Party Services</h2>
              <p className="text-stone-700 leading-relaxed mb-4">
                We work with third-party services that may collect information:
              </p>
              <ul className="list-disc list-inside text-stone-700 space-y-2">
                <li><strong>Affiliate Partners:</strong> Track purchases and attribute commissions</li>
                <li><strong>AI Services:</strong> Process chat messages and images (OpenAI)</li>
                <li><strong>Analytics:</strong> Website traffic and user behavior analysis</li>
                <li><strong>Email Services:</strong> Newsletter delivery and management</li>
                <li><strong>Payment Processors:</strong> Handle transactions securely</li>
              </ul>
              <p className="text-stone-700 leading-relaxed mt-4">
                These third parties have their own privacy policies. We are not responsible for their practices.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-4">6. Data Sharing and Disclosure</h2>
              <p className="text-stone-700 leading-relaxed mb-4">We may share your information:</p>
              <ul className="list-disc list-inside text-stone-700 space-y-2">
                <li>With your consent</li>
                <li>With service providers who assist our operations</li>
                <li>With affiliate partners (anonymous tracking data only)</li>
                <li>To comply with legal obligations</li>
                <li>To protect our rights and prevent fraud</li>
                <li>In connection with a business transfer or merger</li>
              </ul>
              <p className="text-stone-700 leading-relaxed mt-4">
                We do not sell your personal information to third parties.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-4">7. Data Security</h2>
              <p className="text-stone-700 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your data. However, no method 
                of transmission over the internet is 100% secure. While we strive to protect your information, we cannot 
                guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-4">8. Your Rights</h2>
              <p className="text-stone-700 leading-relaxed mb-4">Depending on your location, you may have the following rights:</p>
              <ul className="list-disc list-inside text-stone-700 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                <li><strong>Deletion:</strong> Request deletion of your data</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Data Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
              </ul>
              <p className="text-stone-700 leading-relaxed mt-4">
                To exercise these rights, contact us at contact@gardenandgreenhouse.com.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-4">9. Children's Privacy</h2>
              <p className="text-stone-700 leading-relaxed">
                Our services are not directed to children under 13. We do not knowingly collect personal information from 
                children under 13. If we discover we have collected such information, we will delete it promptly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-4">10. California Privacy Rights</h2>
              <p className="text-stone-700 leading-relaxed">
                California residents have specific rights under the California Consumer Privacy Act (CCPA), including the 
                right to know what personal information is collected, request deletion, and opt-out of the sale of personal 
                information. We do not sell personal information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-4">11. International Users</h2>
              <p className="text-stone-700 leading-relaxed">
                Our servers are located in the United States. If you access our services from outside the US, your information 
                may be transferred to, stored, and processed in the US. By using our services, you consent to this transfer.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-4">12. Changes to This Policy</h2>
              <p className="text-stone-700 leading-relaxed">
                We may update this Privacy Policy periodically. Changes will be posted on this page with an updated 
                "Last Updated" date. We encourage you to review this policy regularly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-4">13. Contact Us</h2>
              <p className="text-stone-700 leading-relaxed">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
                <br />
                <strong>Email:</strong> contact@gardenandgreenhouse.com
                <br />
                <strong>Website:</strong> www.gardenandgreenhouse.com
              </p>
            </section>

            <section className="bg-emerald-50 p-6 rounded-lg mt-8">
              <p className="text-sm text-stone-600">
                <strong>Consent:</strong> By using our website, you consent to our Privacy Policy and agree to its terms.
              </p>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
